import { IntlShape, useIntl } from 'react-intl'

type DeepMessageDict = {
  [key: string]: string | DeepMessageDict
}

/**
 * Translate many messages at once with memoization.
 *
 * Convenience hook that provides typesafe access to multiple messages at once.
 *
 * Usage:
 *
 *     const messageIds = {
 *       title: 'builder.layerPanel.cell',
 *       aggregationSize: 'builder.layerPanel.aggregationSize',
 *       types: { // supports nested objects too
 *         h3: 'builder.layerPanel.h3',
 *         quadbin: 'builder.layerPanel.quadbin'
 *       }
 *     }
 *
 *     function MyComponent({isH3}) {
 *       const messages = useIntlMessages(messageIds)
 *       const index = isH3 ? 'h3' : 'quadbin'
 *       return <div>{messages.title}: {messages.aggregationSize} {messages.types[index]}</div>
 *     }
 */
export function useIntlMessages<T extends DeepMessageDict>(messageIds: T): T {
  const intl = useIntl()
  return staticCachedTranslateDeepMessageIdDict(intl, messageIds)
}

function translateDeepMessageIdDict<T extends DeepMessageDict>(intl: IntlShape, messageIds: T): T {
  const formatMessageDict = <T extends DeepMessageDict>(messageIds: T): T => {
    return Object.entries(messageIds).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: typeof value === 'string' ? intl.formatMessage({ id: value }) : formatMessageDict(value)
      }),
      {} as T
    )
  }
  return formatMessageDict(messageIds)
}

let lastIntl: IntlShape | undefined
let cache: WeakMap<DeepMessageDict, DeepMessageDict>
function staticCachedTranslateDeepMessageIdDict<T extends DeepMessageDict>(intl: IntlShape, messageIds: T): T {
  // this is very naive cache that bases on fact that Intl instance is actually same for most of time
  // so we can reuse those maps across several instances of same components (useMemo can't do that)
  if (lastIntl !== intl) {
    lastIntl = intl
    cache = new WeakMap<DeepMessageDict, DeepMessageDict>()
  }
  const cached = cache.get(messageIds) as T
  if (cached) {
    return cached
  }
  const result = translateDeepMessageIdDict(intl, messageIds)
  cache.set(messageIds, result)
  return result
}
